import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Chip,
  InputAdornment,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalHospital as HospitalIcon,
} from "@mui/icons-material";
import ReferralsTable from "../components/ReferralsTable";
import { getReferralsList, deleteReferral } from "../services/specialistReferralsService";
import { getStatuses, getLocations } from "../../../utils/services/control/masterDataService";

export default function SpecialistReferralsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [referrals, setReferrals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Catalogs from API
  const [urgencyLevels, setUrgencyLevels] = useState([]);
  const [referralStatuses, setReferralStatuses] = useState([]);
  const [followupStatuses, setFollowupStatuses] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [catalogsLoaded, setCatalogsLoaded] = useState(false);

  // Filters — use IDs for API, "" means "All"
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [followUpFilter, setFollowUpFilter] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, referral: null });

  // Load catalogs on mount
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [urgency, status, followup, locs] = await Promise.all([
          getStatuses("specialist_referral_urgency"),
          getStatuses("specialist_referral_status"),
          getStatuses("specialist_referral_followup"),
          getLocations({ clinical: true, status: 1 }),
        ]);
        setUrgencyLevels(urgency || []);
        setReferralStatuses(status || []);
        setFollowupStatuses(followup || []);
        setLocationsList(locs || []);
      } catch (err) {
        console.error("Error loading catalogs:", err);
      } finally {
        setCatalogsLoaded(true);
      }
    };
    loadCatalogs();
  }, []);

  // Load referrals from API
  const loadReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { page, pageSize };
      if (selectedLocationId) filters.locationId = selectedLocationId;
      if (urgencyFilter) filters.urgencyLevelId = urgencyFilter;
      if (statusFilter) filters.referralStatusId = statusFilter;
      if (followUpFilter) filters.followupStatusId = followUpFilter;
      // searchText is handled client-side since API doesn't have text search param

      const result = await getReferralsList(filters);
      setReferrals(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error loading referrals:", err);
      setReferrals([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId, urgencyFilter, statusFilter, followUpFilter, page, pageSize]);

  // Load on mount and when filters change
  useEffect(() => {
    if (catalogsLoaded) loadReferrals();
  }, [loadReferrals, catalogsLoaded]);

  // Check for refresh trigger from create/edit page
  useEffect(() => {
    if (location.state?.refresh) {
      loadReferrals();
      if (location.state?.message) {
        setSnackbar({
          open: true,
          message: location.state.message,
          severity: "success",
        });
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loadReferrals]);

  // Client-side search filter on already-loaded data
  const displayedReferrals = useMemo(() => {
    if (!searchText.trim()) return referrals;
    const search = searchText.toLowerCase();
    return referrals.filter(
      (r) =>
        r.patientName?.toLowerCase().includes(search) ||
        r.cipId?.toLowerCase().includes(search) ||
        r.specialistName?.toLowerCase().includes(search)
    );
  }, [referrals, searchText]);

  const handleNewReferral = () => {
    navigate("/specialist-referrals/create");
  };

  const handleSelectReferral = (referral) => {
    navigate(`/specialist-referrals/edit/${referral.specialistReferralId}`);
  };

  const handleDeleteReferral = (referral) => {
    setDeleteDialog({ open: true, referral });
  };

  const handleConfirmDelete = async () => {
    const { referral } = deleteDialog;
    setDeleteDialog({ open: false, referral: null });
    try {
      await deleteReferral(referral.specialistReferralId);
      setSnackbar({
        open: true,
        message: `Referral for ${referral.patientName} deleted successfully.`,
        severity: "success",
      });
      loadReferrals();
    } catch (err) {
      console.error("Error deleting referral:", err);
      setSnackbar({
        open: true,
        message: "Error deleting referral. Please try again.",
        severity: "error",
      });
    }
  };

  const handleResetFilters = () => {
    setSelectedLocationId("");
    setSearchText("");
    setFollowUpFilter("");
    setUrgencyFilter("");
    setStatusFilter("");
    setPage(1);
  };

  // Build sidebar centers list
  const centersList = useMemo(() => {
    return [
      { locationId: "", name: "All Centers" },
      ...locationsList.map((loc) => ({
        locationId: loc.locationId,
        name: loc.medicalCenter,
      })),
    ];
  }, [locationsList]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Specialist Referrals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage patient referrals to specialists
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewReferral}
          size="large"
        >
          New Referral
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search by member name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Follow Up"
            value={followUpFilter}
            onChange={(e) => { setFollowUpFilter(e.target.value); setPage(1); }}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            {followupStatuses.map((s) => (
              <MenuItem key={s.statusId} value={s.statusId}>
                {s.statusName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Urgency"
            value={urgencyFilter}
            onChange={(e) => { setUrgencyFilter(e.target.value); setPage(1); }}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            {urgencyLevels.map((l) => (
              <MenuItem key={l.statusId} value={l.statusId}>
                {l.statusName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All</MenuItem>
            {referralStatuses.map((s) => (
              <MenuItem key={s.statusId} value={s.statusId}>
                {s.statusName}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
            size="small"
          >
            Reset
          </Button>
        </Stack>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Sidebar - Medical Centers */}
        <Paper sx={{ width: 220, p: 2, flexShrink: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            <HospitalIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
            Medical Centers
          </Typography>
          <Stack spacing={1}>
            {centersList.map((center) => (
              <Chip
                key={center.locationId || "all"}
                label={center.name}
                onClick={() => {
                  setSelectedLocationId(center.locationId);
                  setPage(1);
                }}
                variant={selectedLocationId === center.locationId ? "filled" : "outlined"}
                color={selectedLocationId === center.locationId ? "primary" : "default"}
                sx={{
                  justifyContent: "flex-start",
                  "& .MuiChip-label": { flex: 1 },
                }}
              />
            ))}
          </Stack>
        </Paper>

        {/* Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ReferralsTable
            data={displayedReferrals}
            loading={loading}
            total={searchText.trim() ? displayedReferrals.length : total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onSelectReferral={handleSelectReferral}
            onDeleteReferral={handleDeleteReferral}
          />
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, referral: null })}
      >
        <DialogTitle>Delete Referral</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the referral for{" "}
            <strong>{deleteDialog.referral?.patientName}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, referral: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
